import React, { useEffect, useState } from "react";
import styles from "./statistic.module.css";
import StatisticFormat from "components/statisticFormat/statisticFormat";
import { statistic6 } from "services/statistic/statistic6";
import { BlockStatusFacultyData, YearlySummary } from "modules/statistic";
import { generateCSVContent, downloadFile } from "utils/statistic";
import ErrorFailed from "components/errorFailed/errorFailed";
import Popup from "components/Popup/popup";
import commonStyles from "components/commonStyle/commonStyle.module.css";

export default function Statistic6() {
  const [blockData, setBlockData] = useState<BlockStatusFacultyData>({});
  const [blocks, setBlocks] = useState<string[]>([]);
  const [yearlySummary, setYearlySummary] = useState<YearlySummary>({});
  const [error, setError] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minYear, setMinYear] = useState<number | null>(null);
  const [maxYear, setMaxYear] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      const res = await statistic6(startDate, endDate);
      if (res.success) {
        setBlockData(res.data);
        setBlocks(res.blocks);
        setYearlySummary(res.yearlySummary);

        const years = Object.keys(res.yearlySummary)
          .filter((key) => key !== "" && !isNaN(Number(key)))
          .map((year) => Number(year));

        if (years.length > 0) {
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);

          setMinYear(minYear);
          setMaxYear(maxYear);
        }
      }
    } catch (error) {
      console.error("Fehler beim Laden der Daten:", error);
    }
  };


  const handleFilter = () => {
    if (!startDate || !endDate) {
      setError("Bitte geben Sie sowohl ein Startdatum als auch ein Enddatum ein.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError("Das Startdatum kann nicht nach dem Enddatum liegen.");
      return;
    }

    setIsPopupOpen(false);
    setError(null);
    setIsPopupOpen(false);
    fetchData();
  };

  const handleDeleteFilter = () => {
    setStartDate('');
    setEndDate('');
    setError(null)
    setIsPopupOpen(false);
  };


  const downloadCSV = () => {
    const headers = [
      'Faculty', 'Year',
      ...blocks.flatMap((block) => [
        `Block ${block}`,
        `Alter bis 1 Jahr`
      ]),
      'Gesamt', 'Alter bis 1 Jahr', 'in % v. Gesamt', 'Kundengruppen Anteil'
    ];

    const rows: (string | number)[][] = [];

    Object.entries(yearlySummary)
      .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
      .forEach(([year, summaryData]) => {
        const row = [
          'gesamt',
          year,
          ...blocks.flatMap((block) => {
            const blockSummary = summaryData.blocks.find((b) => b.block === block) || {
              total_bookings: 0,
              under_one_year: 0
            };
            return [
              blockSummary.total_bookings,
              blockSummary.under_one_year
            ];
          }),
          summaryData.total,
          summaryData.under_one_year,
          (summaryData.percentage_under_one_year ?? 0).toFixed(2),
          '100%'
        ];
        rows.push(row);
      });

    Object.entries(blockData).forEach(([faculty, yearsData]) => {
      Object.entries(yearsData)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .forEach(([year, yearData]) => {
          const row = [
            faculty,
            year,
            ...blocks.flatMap((block) => {
              const blockDataForYear = yearData.blocks.find((b) => b.block === block);
              return [
                blockDataForYear ? blockDataForYear.total_bookings : 0,
                blockDataForYear ? blockDataForYear.under_one_year : 0
              ];
            }),
            yearData.total,
            yearData.under_one_year,
            (yearData.percentage_under_one_year ?? 0).toFixed(2),
            (yearData.percentage ?? 0).toFixed(2)
          ];
          rows.push(row);
        });
    });

    const csvContent = generateCSVContent(headers, rows);
    downloadFile(csvContent, "Anlage3b.csv");
  };

  useEffect(() => {
    fetchData();
  }, [isPopupOpen]);

  return (
    <>
      <StatisticFormat
        label="Anlage 3b"
        description={startDate && endDate
          ? `Belegungszahlen der "Flexi" im Zeitraum vom ${startDate} bis ${endDate} Anzahl der Buchungen je Block (Montag bis Freitag)  nach Fakultäten.`
          : `Belegungszahlen der "Flexi" im Zeitraum vom 01.01.${minYear} bis 31.12.${maxYear} Anzahl der Buchungen je Block (Montag bis Freitag)  nach Fakultäten.`
        }
        onFilterButton1Click={() => setIsPopupOpen(true)}
        onButton1Click={downloadCSV}
      >
        <div className={styles.container}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>faculty</th>
                <th>Jahr</th>
                {blocks.map((block) => (
                  <React.Fragment key={block}>
                    <th>Block {block}</th>
                    <th>Alter bis 1 Jahr</th>
                  </React.Fragment>
                ))}
                <th className={styles.gesamt}>Gesamt</th>
                <th>Alter bis 1 Jahr</th>
                <th>in % v. Gesamt</th>
                <th className={styles.gesamt}>Kundengruppen Anteil</th>
              </tr>
            </thead>
            <tbody>

              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={blocks.length * 2 + 6}></td>
              </tr>
              {Object.entries(yearlySummary)
                .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                .filter(([year]) => year)
                .map(([year, summaryData]) => (
                  <tr key={year} className={styles[`year-${(new Date().getFullYear() - parseInt(year)) % 9 + 1}`]}>
                    <td>gesamt</td>
                    <td>{year}</td>
                    {blocks.map((block) => {
                      const blockSummary = summaryData.blocks.find((b) => b.block === block) || {
                        total_bookings: 0,
                        under_one_year: 0,
                      };
                      return (
                        <React.Fragment key={`${year}-${block}`}>
                          <td>{blockSummary.total_bookings}</td>
                          <td>{blockSummary.under_one_year}</td>
                        </React.Fragment>
                      );
                    })}
                    <td>{summaryData.total}</td>
                    <td>{summaryData.under_one_year}</td>
                    <td>{(summaryData.percentage_under_one_year ?? 0).toFixed(2)}%</td>
                    <td>100%</td>
                  </tr>
                ))}

              <tr className={styles.emptyRowNoBorder}>
                <td colSpan={blocks.length * 2 + 6}></td>
              </tr>
              {Object.entries(blockData).map(([faculty, yearsData]) => {
                const currentYear = Math.max(...Object.keys(yearsData).map(Number));

                return (
                  <React.Fragment key={`${faculty}`}>
                    {Object.entries(yearsData)
                      .filter(([year]) => faculty && year)
                      .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
                      .map(([year, yearData]) => (
                        <tr
                          key={`${faculty}-${year}`}
                          className={styles[`year-${(currentYear - parseInt(year)) % 9 + 1}`]}
                        >
                          <td>{faculty}</td>
                          <td>{year}</td>

                          {blocks.map((block) => {
                            const blockDataForYear = yearData.blocks.find((b) => b.block === block);
                            return (
                              <React.Fragment key={`${faculty}-${year}-${block}`}>
                                <td>
                                  {blockDataForYear ? blockDataForYear.total_bookings : 0}
                                </td>
                                <td>
                                  {blockDataForYear ? blockDataForYear.under_one_year : 0}
                                </td>
                              </React.Fragment>
                            );
                          })}

                          <td>{yearData.total}</td>
                          <td>{yearData.under_one_year}</td>
                          <td>{(yearData.percentage_under_one_year ?? 0).toFixed(2)}%</td>
                          <td>{(yearData.percentage ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}

                    <tr className={styles.emptyRow}>
                      {Array.from({ length: blocks.length * 2 + 6 }).map((_, index) => (
                        <td key={`empty-cell-${index}`}></td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>

          </table>
        </div>
      </StatisticFormat>
      <Popup
        title={'Filter'}
        submitLabel={"Einsetzen"}
        closeLabel={"Zurücksetzen"}
        isWithButton={true}
        isOpen={isPopupOpen}
        onClose={handleDeleteFilter} onSubmit={handleFilter}>
        {error && <ErrorFailed error={error} />}

        <div className={commonStyles.formGroupRow}>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className={commonStyles.formGroup}>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </Popup>
    </>
  );
}
